import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchRefollowBatches,
  hasStatusOutcome,
  mergeMemberWaFromPrevious,
  patchRefollowMemberStatus,
  statusOutcomeDisplayLabel,
} from '../../../utils/marketingApi';
import { useRefollowQueuePoll } from './refollowQueuePoll';
import RefollowMemberRow from './RefollowMemberRow';
import RefollowDraftBatch from './RefollowDraftBatch';
import RefollowSourceBatchChip, { collectRefollowSourceBatches } from './RefollowSourceBatchChip';
import RefollowActionBanner from './RefollowActionBanner';
import ActionBannerDock from '../../../components/ActionBannerDock/ActionBannerDock';
import cm from '../CallManager.module.scss';
import s from './Refollow.module.scss';

const QUEUE_PAGE_SIZE = 20;
const ACTION_BANNER_MS = 6500;

const EMPTY_QUEUE_STATS = {
  due_today: 0,
  upcoming: 0,
  draft_batches: 0,
  done_today: 0,
  cancelled_today: 0,
};

/** Keep optimistic last_wa / whatsapp_sent when a soft reload has not caught up yet. */
function mergeRefollowQueueRows(incoming, previous) {
  if (!Array.isArray(incoming)) return [];
  if (!previous?.length) return incoming;
  const prevById = new Map(previous.map((m) => [m.id, m]));
  return incoming.map((row) => {
    const prev = prevById.get(row.id);
    return prev ? mergeMemberWaFromPrevious(prev, row) : row;
  });
}

function queueRowSignature(row) {
  const wa = row?.last_wa;
  return [
    row?.id,
    row?.status,
    row?.scheduled_date,
    wa?.sent_at,
    wa?.delivery_label,
    wa?.status_label,
    row?.last_manual_call?.id ?? row?.last_manual_call?.started_at,
    row?.last_bot_call?.id ?? row?.last_bot_call?.started_at,
  ].join(':');
}

function queueSignature(items) {
  return (items || []).map(queueRowSignature).join('|');
}

/** Due / upcoming: earliest scheduled date (then time) first. */
function compareRefollowDueOrder(a, b) {
  const da = String(a?.scheduled_date || '');
  const db = String(b?.scheduled_date || '');
  if (da !== db) return da.localeCompare(db);
  const ta = String(a?.scheduled_time || '08:00');
  const tb = String(b?.scheduled_time || '08:00');
  if (ta !== tb) return ta.localeCompare(tb);
  return (Number(a?.id) || 0) - (Number(b?.id) || 0);
}

function matchesToolbarSearch(row, query) {
  const t = (query || '').trim().toLowerCase();
  if (!t || t.length < 2) return true;
  const digits = t.replace(/\D/g, '');
  if (digits.length >= 4) {
    const ph = String(row.phone_e164 || '').replace(/\D/g, '');
    return ph.includes(digits);
  }
  const hay = [row.name, row.phone_e164, row.remarks, row.domain, row.category].join(' ').toLowerCase();
  return hay.includes(t);
}

export default function RefollowUpSection({
  showToast,
  onOpenCustomer,
  queueTab: queueTabProp = 'due_today',
  onQueueTabChange,
  onStatsRefresh,
  refreshTick = 0,
  searchQuery = '',
  queueStats = null,
  queueStatsLoading = false,
}) {
  const [queueTabInternal, setQueueTabInternal] = useState(queueTabProp);
  const queueTab = onQueueTabChange ? queueTabProp : queueTabInternal;
  const setQueueTab = onQueueTabChange || setQueueTabInternal;

  useEffect(() => {
    if (onQueueTabChange) return;
    setQueueTabInternal(queueTabProp);
  }, [queueTabProp, onQueueTabChange]);

  const summary = queueStats ?? EMPTY_QUEUE_STATS;

  const [members, setMembers] = useState([]);
  const [queuePage, setQueuePage] = useState(1);
  const [queueMeta, setQueueMeta] = useState({ total: 0, total_pages: 1 });
  const [memberLoading, setMemberLoading] = useState(false);
  const membersLoadedOnce = useRef(false);
  const loadQueueReq = useRef(0);
  const skipRefreshTickRef = useRef(true);
  const [draftBatches, setDraftBatches] = useState([]);
  const [draftLoading, setDraftLoading] = useState(false);
  const [openDraftId, setOpenDraftId] = useState(null);
  const [sourceBatchFilter, setSourceBatchFilter] = useState(null);
  const [recentAction, setRecentAction] = useState(null);
  const recentActionTimerRef = useRef(null);

  const applyQueueData = useCallback((data, { merge = false } = {}) => {
    setMembers((prev) => {
      const next = mergeRefollowQueueRows(data?.items || [], merge ? prev : []);
      if (queueSignature(next) === queueSignature(prev)) return prev;
      return next;
    });
    setQueueMeta((prev) => {
      const total = Number(data?.total) || 0;
      const total_pages = Math.max(1, Number(data?.total_pages) || 1);
      if (prev.total === total && prev.total_pages === total_pages) return prev;
      return { total, total_pages };
    });
    membersLoadedOnce.current = true;
  }, []);

  const onQueueFetchError = useCallback((e) => {
    showToast?.(e.message || 'Failed to load queue', 'error');
    setMembers([]);
    setQueueMeta({ total: 0, total_pages: 1 });
  }, [showToast]);

  const refetchQueue = useRefollowQueuePoll({
    enabled: queueTab !== 'draft',
    scope: queueTab,
    page: queuePage,
    pageSize: QUEUE_PAGE_SIZE,
    sourceBatchId: sourceBatchFilter,
    applyData: applyQueueData,
    onError: onQueueFetchError,
    setMemberLoading,
    loadReqRef: loadQueueReq,
    membersLoadedOnceRef: membersLoadedOnce,
  });
  const loadDrafts = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setDraftLoading(true);
    try {
      const data = await fetchRefollowBatches({ state: 'draft' });
      setDraftBatches(data?.items || []);
    } catch (e) {
      if (!silent) {
        showToast?.(e.message || 'Failed to load drafts', 'error');
        setDraftBatches([]);
      }
    } finally {
      if (!silent) setDraftLoading(false);
    }
  }, [showToast]);

  useEffect(() => () => {
    if (recentActionTimerRef.current) window.clearTimeout(recentActionTimerRef.current);
  }, []);

  useEffect(() => {
    if (queueTab === 'draft') {
      loadDrafts();
      setMembers([]);
      membersLoadedOnce.current = false;
    }
  }, [queueTab, loadDrafts]);

  useEffect(() => {
    if (skipRefreshTickRef.current) {
      skipRefreshTickRef.current = false;
      return;
    }
    if (!refreshTick) return;
    if (queueTab === 'draft') loadDrafts({ silent: true });
    else refetchQueue({ merge: true, silent: true });
  }, [refreshTick, loadDrafts, queueTab, refetchQueue]);

  const afterDraftChange = useCallback(() => {
    loadDrafts({ silent: true });
    onStatsRefresh?.();
  }, [loadDrafts, onStatsRefresh]);

  const flashRecentAction = useCallback((row, { outcome, status, outcomeLabel }) => {
    setRecentAction({
      key: row.id,
      memberId: row.id,
      phone: row.phone_e164,
      memberName: row.name,
      outcome,
      status,
      outcomeLabel: outcomeLabel || statusOutcomeDisplayLabel(outcome) || status,
    });
    if (recentActionTimerRef.current) window.clearTimeout(recentActionTimerRef.current);
    recentActionTimerRef.current = window.setTimeout(() => {
      setRecentAction(null);
      recentActionTimerRef.current = null;
    }, ACTION_BANNER_MS);
  }, []);

  const patchRefollowRow = useCallback((rowId, patch) => {
    if (!rowId || !patch) return;
    const waOnly = (patch.whatsapp_sent || patch.last_wa) && !patch.outcome;
    let rowAfter = null;
    setMembers((prev) => prev.map((m) => {
      if (Number(m.id) !== Number(rowId)) return m;
      rowAfter = { ...m, ...patch };
      return rowAfter;
    }));
    if (waOnly || !patch.outcome || !hasStatusOutcome(patch.outcome)) return;
    if (rowAfter) {
      flashRecentAction(rowAfter, {
        outcome: patch.outcome,
        outcomeLabel: patch.outcome_label || statusOutcomeDisplayLabel(patch.outcome),
      });
    }
  }, [flashRecentAction]);

  const refreshQueueSoft = useCallback((opts) => {
    if (opts?.soft) return;
    if (queueTab !== 'draft') refetchQueue({ merge: true, silent: true });
  }, [refetchQueue, queueTab]);

  const scheduleQueueRefresh = useCallback(() => {
    if (queueTab !== 'draft') refetchQueue({ merge: true, silent: true });
    onStatsRefresh?.();
  }, [refetchQueue, onStatsRefresh, queueTab]);

  const patchStatus = async (row, status) => {
    try {
      await patchRefollowMemberStatus(row.id, status);
      flashRecentAction(row, {
        status,
        outcomeLabel: status === 'done' ? 'Done' : 'Cancelled',
      });
      window.requestAnimationFrame(() => {
        setMembers((prev) => prev.filter((m) => m.id !== row.id));
      });
      scheduleQueueRefresh();
    } catch (e) {
      showToast?.(e.message || 'Update failed', 'error');
    }
  };

  const editRecentAction = async () => {
    if (!recentAction?.memberId) return;
    const queueStatus = recentAction.status;
    if (queueStatus !== 'done' && queueStatus !== 'cancelled') {
      setRecentAction(null);
      if (recentActionTimerRef.current) window.clearTimeout(recentActionTimerRef.current);
      showToast?.('Use Edit on the row to change status', 'info');
      return;
    }
    try {
      await patchRefollowMemberStatus(recentAction.memberId, 'active');
      setRecentAction(null);
      if (recentActionTimerRef.current) window.clearTimeout(recentActionTimerRef.current);
      refetchQueue({ merge: false, silent: false });
      onStatsRefresh?.();
      showToast?.('Returned to queue — you can mark Done or Cancel again', 'info');
    } catch (e) {
      showToast?.(e.message || 'Could not undo', 'error');
    }
  };

  const toggleSourceBatchFilter = (batchId) => {
    setQueuePage(1);
    setSourceBatchFilter((prev) => (prev === batchId ? null : batchId));
  };

  const tabs = [
    { key: 'due_today', label: 'Due today', count: summary.due_today },
    { key: 'upcoming', label: 'Upcoming', count: summary.upcoming },
    { key: 'draft', label: 'Draft', count: summary.draft_batches },
  ];

  const visibleMembers = useMemo(() => {
    const filtered = members.filter((row) => matchesToolbarSearch(row, searchQuery));
    if (queueTab === 'due_today' || queueTab === 'upcoming') {
      return [...filtered].sort(compareRefollowDueOrder);
    }
    return filtered;
  }, [members, searchQuery, queueTab]);

  const queueSourceBatches = useMemo(
    () => collectRefollowSourceBatches(members),
    [members],
  );

  const tabBadge = (count) => {
    if (queueStatsLoading && !queueStats) return '…';
    return count;
  };

  const showInitialLoading = memberLoading && members.length === 0;
  const showEmpty = !showInitialLoading && visibleMembers.length === 0;

  return (
    <div className={s.followUpShell}>
      <div className={s.followUpToolbar}>
        <div className={s.followUpToolbarMain}>
          <div className={s.followUpTabs} role="tablist" aria-label="Follow-up queue">
            {tabs.map(({ key, label, count }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={queueTab === key}
                className={`${s.followUpTab}${queueTab === key ? ` ${s.followUpTabActive}` : ''}`}
                onClick={() => {
                  setQueuePage(1);
                  setQueueTab(key);
                }}
              >
                {label}
                <span className={s.followUpTabBadge}>{tabBadge(count)}</span>
              </button>
            ))}
          </div>
          {queueTab !== 'draft' && queueSourceBatches.length > 0 ? (
            <div className={s.followUpSourceChipRow} aria-label="Call Manager batches in this queue">
              {queueSourceBatches.map((b) => (
                <RefollowSourceBatchChip
                  key={b.id}
                  opsBatchId={b.id}
                  name={b.name}
                  description={b.description}
                  filterActive={sourceBatchFilter === b.id}
                  onFilterToggle={toggleSourceBatchFilter}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {queueTab !== 'draft' ? (
        <ActionBannerDock action={recentAction}>
          <RefollowActionBanner action={recentAction} onEdit={editRecentAction} />
        </ActionBannerDock>
      ) : null}

      {queueTab === 'draft' ? (
        draftLoading && !draftBatches.length ? (
          <div className={cm.loading}>Loading drafts…</div>
        ) : draftBatches.length === 0 ? (
          <div className={cm.empty}>No draft batches.</div>
        ) : (
          <div className={cm.batchList}>
            {draftBatches.map((b) => (
              <RefollowDraftBatch
                key={b.id}
                batch={b}
                expanded={openDraftId === b.id}
                onToggle={(id) => setOpenDraftId((prev) => (prev === id ? null : id))}
                onPublished={() => {
                  setOpenDraftId(null);
                  afterDraftChange();
                  setQueueTab('due_today');
                  setQueuePage(1);
                  refetchQueue({ merge: false, silent: false });
                }}
                onDraftEmpty={(id) => {
                  if (openDraftId === id) setOpenDraftId(null);
                  afterDraftChange();
                }}
                showToast={showToast}
                onOpenCustomer={onOpenCustomer}
                refreshTick={refreshTick}
              />
            ))}
          </div>
        )
      ) : (
        <div className={s.followUpQueueBody}>
          {showInitialLoading ? (
            <div className={cm.loading}>Loading…</div>
          ) : null}
          {showEmpty ? (
            <div className={cm.empty}>
              {members.length && sourceBatchFilter != null
                ? 'No follow-ups in this CM batch.'
                : members.length && searchQuery.trim().length >= 2
                  ? 'No follow-ups match your search.'
                  : 'No customers in this queue.'}
            </div>
          ) : null}
          {visibleMembers.length > 0 ? (
            <div className={cm.memberList}>
              {visibleMembers.map((row) => (
                <RefollowMemberRow
                  key={row.id}
                  row={row}
                  showToast={showToast}
                  recentActionKey={recentAction?.key}
                  onPatchRow={patchRefollowRow}
                  onRefresh={refreshQueueSoft}
                  onOpenCustomer={() => onOpenCustomer?.(row)}
                  onDone={(r) => patchStatus(r, 'done')}
                  onCancelled={(r) => patchStatus(r, 'cancelled')}
                  sourceBatchFilter={sourceBatchFilter}
                  onSourceBatchFilterToggle={toggleSourceBatchFilter}
                />
              ))}
            </div>
          ) : null}
          {queueMeta.total_pages > 1 && (members.length || !memberLoading) ? (
            <div className={cm.pagination}>
              <button
                type="button"
                className={cm.pagerBtn}
                disabled={queuePage <= 1}
                onClick={() => setQueuePage((p) => Math.max(1, p - 1))}
              >
                Prev
              </button>
              <span>
                Page {queuePage} of {queueMeta.total_pages}
                {queueMeta.total ? ` · ${queueMeta.total} customers` : ''}
              </span>
              <button
                type="button"
                className={cm.pagerBtn}
                disabled={queuePage >= queueMeta.total_pages}
                onClick={() => setQueuePage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
